import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, GridLegacy, Paper, Link } from '@mui/material';

interface TourismItem {
   title: string;
   description?: string;
   link?: string;
}

const createItem = (title: string, description?: string, link?: string): TourismItem => ({
   title,
   description,
   link,
});

const getNature = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.nature.subsections.pfaueninsel.title'),
      t('tourism.sections.nature.subsections.pfaueninsel.description'),
   ),
   createItem(
      t('tourism.sections.nature.subsections.lacs_parcs.title'),
      t('tourism.sections.nature.subsections.lacs_parcs.description'),
      'https://www.visitberlin.de/fr/lacs-plages-berlin',
   ),
   createItem(
      t('tourism.sections.nature.subsections.viktoriapark.title'),
      t('tourism.sections.nature.subsections.viktoriapark.description'),
   ),
   createItem(
      t('tourism.sections.nature.subsections.tiergarten.title'),
      t('tourism.sections.nature.subsections.tiergarten.description'),
   ),
   createItem(
      t('tourism.sections.nature.subsections.grunewald.title'),
      t('tourism.sections.nature.subsections.grunewald.description'),
   ),
];

const getEnfants = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.children.subsections.naturkundemuseum.title'),
      t('tourism.sections.children.subsections.naturkundemuseum.description'),
      'https://www.museumfuernaturkunde.berlin/en',
   ),
   createItem(
      t('tourism.sections.children.subsections.legoland.title'),
      t('tourism.sections.children.subsections.legoland.description'),
      'https://www.legolanddiscoverycentre.com/berlin/en/',
   ),
   createItem(
      t('tourism.sections.children.subsections.musee_espionnage.title'),
      t('tourism.sections.children.subsections.musee_espionnage.description'),
      'https://www.deutsches-spionagemuseum.de/en',
   ),
   createItem(
      t('tourism.sections.children.subsections.zoo_aquarium.title'),
      t('tourism.sections.children.subsections.zoo_aquarium.description'),
      'https://www.zoo-berlin.de/fr/',
   ),
   createItem(
      t('tourism.sections.children.subsections.accrobranche.title'),
      t('tourism.sections.children.subsections.accrobranche.description'),
      'https://beachmitte.de/mountmitte-hochseilgarten-berlin-mitte/',
   ),
   createItem(
      t('tourism.sections.children.subsections.trampolinpark.title'),
      t('tourism.sections.children.subsections.trampolinpark.description'),
      'https://www.sprungraum.de/berlin',
   ),
];

const getGratuit = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.free.subsections.reichstag.title'),
      t('tourism.sections.free.subsections.reichstag.description'),
      'https://www.bundestag.de/fr/visite/coupole',
   ),
   createItem(
      t('tourism.sections.free.subsections.east_side_gallery.title'),
      t('tourism.sections.free.subsections.east_side_gallery.description'),
      'https://www.visitberlin.de/fr/east-side-gallery',
   ),
   createItem(
      t('tourism.sections.free.subsections.musees_gratuits.title'),
      t('tourism.sections.free.subsections.musees_gratuits.description'),
      'https://www.visitberlin.de/fr/musees-gratuits',
   ),
];

const getInsolite = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.unusual.subsections.bunker_lasertag.title'),
      t('tourism.sections.unusual.subsections.bunker_lasertag.description'),
      'https://www.lasergame-berlin.de/',
   ),
   createItem(
      t('tourism.sections.unusual.subsections.laughing_spree.title'),
      t('tourism.sections.unusual.subsections.laughing_spree.description'),
      'https://www.laughingspree.de/',
   ),
   createItem(
      t('tourism.sections.unusual.subsections.berliner_unterwelten.title'),
      t('tourism.sections.unusual.subsections.berliner_unterwelten.description'),
      'https://www.berliner-unterwelten.de/',
   ),
   createItem(
      t('tourism.sections.unusual.subsections.balancoire_alexanderplatz.title'),
      t('tourism.sections.unusual.subsections.balancoire_alexanderplatz.description'),
   ),
   createItem(
      t('tourism.sections.unusual.subsections.montgolfiere.title'),
      t('tourism.sections.unusual.subsections.montgolfiere.description'),
      'https://www.weltballon.de/en/',
   ),
   createItem(
      t('tourism.sections.unusual.subsections.musee_bureaucratie.title'),
      t('tourism.sections.unusual.subsections.musee_bureaucratie.description'),
   ),
   createItem(
      t('tourism.sections.unusual.subsections.hackescher_markt.title'),
      t('tourism.sections.unusual.subsections.hackescher_markt.description'),
      'https://www.kaethe-wohlfahrt.com/',
   ),
   createItem(
      t('tourism.sections.unusual.subsections.tempelhofer_feld.title'),
      t('tourism.sections.unusual.subsections.tempelhofer_feld.description'),
      'https://www.tempelhoferfeld.berlin/',
   ),
];

const getTouristique = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.touristic.subsections.brandenburger_tor.title'),
      t('tourism.sections.touristic.subsections.brandenburger_tor.description'),
      'https://www.visitberlin.de/fr/porte-de-brandebourg',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.museuminsel.title'),
      t('tourism.sections.touristic.subsections.museuminsel.description'),
      'https://www.visitberlin.de/fr/lile-aux-musees',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.alexanderplatz.title'),
      t('tourism.sections.touristic.subsections.alexanderplatz.description'),
      'https://www.visitberlin.de/fr/alexanderplatz',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.checkpoint_charlie.title'),
      t('tourism.sections.touristic.subsections.checkpoint_charlie.description'),
      'https://www.visitberlin.de/fr/checkpoint-charlie',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.east_side_gallery.title'),
      t('tourism.sections.touristic.subsections.east_side_gallery.description'),
      'https://www.visitberlin.de/fr/east-side-gallery',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.bus_100_200.title'),
      t('tourism.sections.touristic.subsections.bus_100_200.description'),
   ),
   createItem(
      t('tourism.sections.touristic.subsections.spree_boat_ride.title'),
      t('tourism.sections.touristic.subsections.spree_boat_ride.description'),
      'https://www.sternundkreis.de/',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.trabant_ride.title'),
      t('tourism.sections.touristic.subsections.trabant_ride.description'),
      'https://trabi-safari.de/',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.doner_curry_schnitzel.title'),
      t('tourism.sections.touristic.subsections.doner_curry_schnitzel.description'),
   ),
   createItem(
      t('tourism.sections.touristic.subsections.ddr_museum.title'),
      t('tourism.sections.touristic.subsections.ddr_museum.description'),
      'https://www.ddr-museum.de/en',
   ),
   createItem(
      t('tourism.sections.touristic.subsections.kurfuerstendamm.title'),
      t('tourism.sections.touristic.subsections.kurfuerstendamm.description'),
   ),
   createItem(
      t('tourism.sections.touristic.subsections.nikolaiviertel.title'),
      t('tourism.sections.touristic.subsections.nikolaiviertel.description'),
      'https://www.visitberlin.de/fr/nikolaiviertel',
   ),
];

const getFestif = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.festive.subsections.berghain.title'),
      t('tourism.sections.festive.subsections.berghain.description'),
      'https://ra.co/clubs/4396',
   ),
   createItem(
      t('tourism.sections.festive.subsections.warschauer_strasse.title'),
      t('tourism.sections.festive.subsections.warschauer_strasse.description'),
   ),
];

const getShopping = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.shopping.subsections.mall_of_berlin.title'),
      t('tourism.sections.shopping.subsections.mall_of_berlin.description'),
      'https://www.mallofberlin.de/en',
   ),
   createItem(
      t('tourism.sections.shopping.subsections.alexa.title'),
      t('tourism.sections.shopping.subsections.alexa.description'),
      'https://www.alexacentre.com/en/',
   ),
   createItem(
      t('tourism.sections.shopping.subsections.schlossstrasse.title'),
      t('tourism.sections.shopping.subsections.schlossstrasse.description'),
   ),
   createItem(
      t('tourism.sections.shopping.subsections.dong_xuan_center.title'),
      t('tourism.sections.shopping.subsections.dong_xuan_center.description'),
   ),
   createItem(
      t('tourism.sections.shopping.subsections.friedrichstrasse_dussmann.title'),
      t('tourism.sections.shopping.subsections.friedrichstrasse_dussmann.description'),
      'https://www.kulturkaufhaus.de/',
   ),
   createItem(
      t('tourism.sections.shopping.subsections.kurfuerstendamm_europa_center.title'),
      t('tourism.sections.shopping.subsections.kurfuerstendamm_europa_center.description'),
   ),
];

const getFavoris = (t: (key: string) => string): TourismItem[] => [
   createItem(
      t('tourism.sections.favorites.subsections.opera_berlin.title'),
      t('tourism.sections.favorites.subsections.opera_berlin.description'),
      'https://www.staatsoper-berlin.de/',
   ),
   createItem(
      t('tourism.sections.favorites.subsections.krumme_lanke_schlachtensee.title'),
      t('tourism.sections.favorites.subsections.krumme_lanke_schlachtensee.description'),
   ),
   createItem(
      t('tourism.sections.favorites.subsections.cafes_spree_hackescher.title'),
      t('tourism.sections.favorites.subsections.cafes_spree_hackescher.description'),
   ),
   createItem(
      t('tourism.sections.favorites.subsections.dreamz_kebab.title'),
      t('tourism.sections.favorites.subsections.dreamz_kebab.description'),
   ),
   createItem(
      t('tourism.sections.favorites.subsections.havanna.title'),
      t('tourism.sections.favorites.subsections.havanna.description'),
   ),
   createItem(
      t('tourism.sections.favorites.subsections.nibs_cacao_bleibtreustr.title'),
      t('tourism.sections.favorites.subsections.nibs_cacao_bleibtreustr.description'),
   ),
   createItem(
      t('tourism.sections.favorites.subsections.hundestrand_grunewald.title'),
      t('tourism.sections.favorites.subsections.hundestrand_grunewald.description'),
   ),
   createItem(
      t('tourism.sections.favorites.subsections.schloss_sanssouci.title'),
      t('tourism.sections.favorites.subsections.schloss_sanssouci.description'),
      'https://www.spsg.de/en/palaces-gardens/object/sanssouci-palace/',
   ),
   createItem(
      t('tourism.sections.favorites.subsections.schloss_charlottenburg.title'),
      t('tourism.sections.favorites.subsections.schloss_charlottenburg.description'),
      'https://www.visitberlin.de/fr/chateau-de-charlottenburg',
   ),
   createItem(
      t('tourism.sections.favorites.subsections.dussmann.title'),
      t('tourism.sections.favorites.subsections.dussmann.description'),
      'https://www.kulturkaufhaus.de/',
   ),
];

const Section = ({ title, items, id }: { title: string; items: TourismItem[]; id?: string }) => (
   <Box sx={{ marginBottom: 10 }} id={id}>
      <Typography variant="h5" gutterBottom>
         {title}
      </Typography>
      <GridLegacy container spacing={2} alignItems="stretch">
         {items.map((item, index) => (
            <GridLegacy item xs={12} sm={6} md={4} key={index} mt={3}>
               <Paper
                  sx={{
                     padding: 2,
                     height: '100%',
                     display: 'flex',
                     flexDirection: 'column',
                     gap: 1.5,
                  }}
               >
                  <Typography variant="subtitle1" fontWeight={600}>
                     {item.link ? (
                        <Link href={item.link} target="_blank" rel="noopener noreferrer">
                           {item.title}
                        </Link>
                     ) : (
                        item.title
                     )}
                  </Typography>
                  {item.description && (
                     <Typography variant="body2" color="text.secondary">
                        {item.description}
                     </Typography>
                  )}
               </Paper>
            </GridLegacy>
         ))}
      </GridLegacy>
   </Box>
);

export const Tourisme = () => {
   const { t } = useTranslation();
   return (
      <Box sx={{ padding: '2rem' }}>
         <Typography variant="h4" mt={3} gutterBottom>
            {t('tourism.title')}
         </Typography>

         <Section title={t('tourism.sections.nature.title')} items={getNature(t)} id="nature" />
         <Section title={t('tourism.sections.children.title')} items={getEnfants(t)} id="enfant" />
         <Section title={t('tourism.sections.free.title')} items={getGratuit(t)} id="gratuit" />
         <Section
            title={t('tourism.sections.unusual.title')}
            items={getInsolite(t)}
            id="insolite"
         />
         <Section
            title={t('tourism.sections.touristic.title')}
            items={getTouristique(t)}
            id="touristique"
         />
         <Section title={t('tourism.sections.festive.title')} items={getFestif(t)} id="festif" />
         <Section
            title={t('tourism.sections.shopping.title')}
            items={getShopping(t)}
            id="shopping"
         />
         <Section
            title={t('tourism.sections.favorites.title')}
            items={getFavoris(t)}
            id="favoris"
         />
      </Box>
   );
};
